<template>
    <div> 
        <ConfirmDialog />
        <div class="flex mb-2">
            <div class="flex-0 flex align-items-center justify-content-center">
              <div class="px-0 py-2">
                <h2 class="m-0 p-0 font-light text-600">Complaints for {{ selectedEmployee?.employee_name || 'All Employees' }}</h2>
                <p class="p-0 m-0 pt-1 text-xs text-400"> Showing {{ complaints.length }} results.</p>
              </div>
            </div>
            <div class="flex-1 flex align-items-center gap-2 justify-content-end">
                <InputText
                    v-model="quickSearch"
                    placeholder="Quick Search..."
                    class="w-full md:w-10rem"
                    :showClear="true"
                    @input="onQuickSearch" 
                />
                <Calendar 
                    v-model="selectedDate" 
                    view="month" 
                    dateFormat="MM yy" 
                    class="w-full md:w-12rem"
                    :disabled="!!selectedEmployee || !!selectedCompany"
                    @date-select="onDateChange"
                />
                <Button icon="pi pi-plus" @click="openNewComplaintDialog" />
            </div>
        </div>
    </div>
   <div class="flex complaints-view">
        <div class="flex-none mr-2 w-4">
            <DataTable 
                :value="filteredComplaints" 
                dataKey="id" 
                v-model:selection="selectedComplaint" 
                class="w-full" 
                selectionMode="single" 
                :loading="loading || ticketLoading" 
                @row-click="onRowClick"
            >
                <Column field="subject" header="Subject" class="w-full">
                    <template #body="slotProps">
                        <div class="flex flex-column gap-1">
                          <span class="text-xs text-400">{{ formatFirebaseDate(slotProps.data.complaint_date || slotProps.data.createdby.timestamp) }}</span>
                          <span class="font-bold">{{ slotProps.data.subject || 'No Subject' }}</span>
                          <span class="text-xs text-400">{{ slotProps.data.employee.name }} - {{ slotProps.data.company.name }}</span>
                        </div>
                    </template>
                </Column>
                <Column field="status" header="Status">
                    <template #body="slotProps">
                        <div class="flex items-center">
                        <span class="ml-2 flex items-center">
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24"
                                :fill="slotProps.data.hasUnreadPost ? '#888888' : '#3B82F6'" 
                                stroke-width="0"
                                :title="slotProps.data.hasUnreadPost ? 'Unread' : 'Read'"
                            >
                                <path d="M18 7l-8 8-4-4-1.5 1.5L9 17l9.5-9.5z" stroke="currentColor" />
                                <path d="M13 7l-8 8-4-4-1.5 1.5L4 17l9.5-9.5z" stroke="currentColor" />
                            </svg>
                        </span>
                        <span :class="{
                            'px-2 py-1 text-xs border-round': true,
                            'bg-yellow-500 text-yellow-900': slotProps.data.mailer_status === false,
                            'bg-green-800 text-50': slotProps.data.status === 'open' && slotProps.data.mailer_status === true,
                            'bg-gray-700 text-400': slotProps.data.status === 'closed' && slotProps.data.mailer_status === true,
                        }">
                            {{ slotProps.data.mailer_status ? slotProps.data.status : 'Draft'  }} 
                        </span>
                      </div>
                    </template>
                </Column>
                <template #empty>
                    <div class="text-500 text-sm">No Complaint found for this filter.</div>
                </template>
            </DataTable>   
        </div>
        <div class="flex-grow-1 bg-gray-800 p-3 border-round">
            <PreviewTicket 
                v-if="(routeTicketId || selectedComplaint?.id) && (routeEmployeeId || selectedComplaint?.employee?.id)"
                :ticketId="routeTicketId || selectedComplaint?.id"
                :employeeId="routeEmployeeId || selectedComplaint?.employee?.id"
                :isStandalone="isDetailView"
                v-model:showingReconcileForm="showingReconcileForm"
                @delete-complaint="handleDeleteComplaint"
                @complaint-reconciled="onComplaintReconciled"
                @post-published="handlePostUpdates"
                @post-deleted="handlePostUpdates"
                @complaint-closed="onComplaintClosed"
                @back-to-list="handleBackToList"
                @loading-state-changed="handleTicketLoadingChange"
            />
            <div v-else class="flex align-items-center justify-content-center h-full">
                <div class="text-center">
                    <i class="pi pi-inbox text-xl text-400 mb-3" style="font-size: 3rem"></i>
                    <p class="text-400">Select a complaint to view details</p>
                </div>
            </div>
        </div>    
    </div>

    <NewComplaintDialog 
        v-model="displayComplaintDialog"
        :preSelectedEmployeeId="selectedEmployee?.employee_id"
        @complaint-created="onComplaintCreated"
    />
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEmployeesStore } from '@/stores/employees';
import { Timestamp } from 'firebase/firestore';
import { useComplaints } from '@/composables/complaints/useComplaints';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import NewComplaintDialog from '@/components/complaints/NewComplaintDialog.vue';
import Calendar from 'primevue/calendar';
import ConfirmDialog from 'primevue/confirmdialog';
import InputText from 'primevue/inputtext';
import PreviewTicket from '@/components/complaints/PreviewTicket.vue';
import { useRouter, useRoute } from 'vue-router';

const route = useRoute();
const router = useRouter();

defineProps({
  year: {
    type: String,
    default: null
  },
  month: {
    type: String,
    default: null
  },
  employeeId: {
    type: String,
    default: null
  },
  ticketId: {
    type: String,
    default: null
  }
});

// Get date, ticket ID and employee ID from route params
const routeYear = computed(() => route.params.year);
const routeMonth = computed(() => route.params.month);
const routeTicketId = computed(() => route.params.ticketId);
const routeEmployeeId = computed(() => route.params.employeeId);

const selectedCompany = ref(null);

// Determine if we're in detail view mode
const isDetailView = computed(() => !!routeTicketId.value);

// Create a date object from route params if available
const routeDate = computed(() => {
  if (routeYear.value && routeMonth.value) {
    return new Date(parseInt(routeYear.value), parseInt(routeMonth.value) - 1, 1);
  }
  return null;
});

const {
    complaints,
    loading,
    selectedComplaint,
    fetchComplaints,
    getFilteredComplaints,
} = useComplaints();

const searchQuery = ref('');
const selectedEmployee = ref(null);
const displayComplaintDialog = ref(false);


const employeesStore = useEmployeesStore();
const { employeeNames } = storeToRefs(employeesStore);

const quickSearch = ref('');

const filteredComplaints = computed(() => {
    const complaints = getFilteredComplaints(searchQuery.value);
    if (!quickSearch.value) return complaints;
    
    const searchTerm = quickSearch.value.toLowerCase();
    return complaints.filter(complaint => 
        complaint.subject?.toLowerCase().includes(searchTerm) ||
        complaint.employee?.name?.toLowerCase().includes(searchTerm) ||
        complaint.posts?.some(post => post.message?.toLowerCase().includes(searchTerm)) ||
        complaint.id?.toString().includes(searchTerm)  // Search by complaint ID
    );
});

const openNewComplaintDialog = () => {
  displayComplaintDialog.value = true;
};

const onComplaintCreated = async (newComplaintData) => {
    // Fetch fresh data to update the complaints list
    await fetchComplaints();
    
    if (newComplaintData && newComplaintData.ticket_id) {
        // Extract year and month from ticket_date (format: "YYYY-MM")
        let year, month;
        if (newComplaintData.ticket_date) {
            const dateParts = newComplaintData.ticket_date.split('-');
            year = dateParts[0];
            month = dateParts[1];
        } else {
            // Fallback to current date if ticket_date is not provided
            const now = new Date();
            year = now.getFullYear();
            month = (now.getMonth() + 1).toString().padStart(2, '0');
        }
        
        // Navigate to the newly created complaint using the data from the API response
        router.push({
            name: 'ViewComplaintDetail',
            params: {
                year: year,
                month: month,
                employeeId: newComplaintData.employee_id || '0',
                ticketId: newComplaintData.ticket_id
            }
        });
        
        // The selectedComplaint will be set by the component when it loads the detail view
    }
};

const ticketLoading = ref(false);

const onRowClick = (event) => {
  if (ticketLoading.value) return; // Prevent clicking while loading
  
  // Check if clicking on already selected complaint
  if (selectedComplaint.value && selectedComplaint.value.id === event.data.id) {
    // Deselect the complaint
    selectedComplaint.value = null;
    handleBackToList();
    return;
  }
  
  selectedComplaint.value = event.data;
  ticketLoading.value = true; // Set loading state
  
  // Get current date or use the one from route
  const currentDate = routeDate.value || new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
  
  // Navigate to the complaint detail page with the new URL structure
  router.push({
    name: 'ViewComplaintDetail',
    params: {
      year: year,
      month: month.toString().padStart(2, '0'), // Ensure two digits
      employeeId: event.data.employee?.id || 0,
      ticketId: event.data.id
    }
  });
};

const showingReconcileForm = ref(false);

const onComplaintReconciled = async (updatedComplaint) => {
    // Fetch fresh data to update the complaints list
    await fetchComplaints();
    
    // Update the selectedComplaint with the latest data from the complaints array
    if (updatedComplaint && updatedComplaint.id) {
        // Find the updated complaint in the refreshed complaints array
        const freshComplaint = complaints.value.find(c => c.id === updatedComplaint.id);
        if (freshComplaint) {
            selectedComplaint.value = freshComplaint;
        } else {
            selectedComplaint.value = updatedComplaint;
        }
    }
};

const onComplaintClosed = async (updatedComplaint) => {
    // Fetch fresh data to update the complaints list
    await fetchComplaints();
    
    // Update the selectedComplaint with the latest data from the complaints array
    if (updatedComplaint && updatedComplaint.id) {
        // Find the updated complaint in the refreshed complaints array
        const freshComplaint = complaints.value.find(c => c.id === updatedComplaint.id);
        if (freshComplaint) {
            selectedComplaint.value = freshComplaint;
        } else {
            selectedComplaint.value = updatedComplaint;
        }
    }
};

const handlePostUpdates = async () => {
    console.log('handlePostUpdates');
    // Fetch fresh data to update the complaints list
    await fetchComplaints();
    
};

const selectedDate = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

const onDateChange = () => {
    selectedComplaint.value = null;
    quickSearch.value = '';
    
    // Navigate to the year-month URL
    const year = selectedDate.value.getFullYear();
    const month = (selectedDate.value.getMonth() + 1).toString().padStart(2, '0'); // Convert to 1-indexed and ensure 2 digits
    
    router.push({
        name: 'ViewComplaintsByMonth',
        params: {
            year: year,
            month: month
        }
    });
    
};

const fetchEmployeeData = async () => {
  try {
    await employeesStore.ensureEmployees();
  } catch (error) {
    console.error('Error fetching employees:', error);
  }
};

const formatFirebaseDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } else if (typeof timestamp === 'string') {
    // If the timestamp is stored as an ISO string
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  return 'Invalid Date';
};

const handleDeleteComplaint = async (navigationInfo) => {
  // Clear the selected complaint
  selectedComplaint.value = null;
  
  // If we received navigation info from the child component
  if (navigationInfo && navigationInfo.year && navigationInfo.month) {
    console.log('Received navigation info:', navigationInfo);
    const { year, month } = navigationInfo;
    
    // Navigate to the month view
    console.log(`Navigating to year: ${year}, month: ${month}`);
    await router.push({
      name: 'ViewComplaintsByMonth',
      params: { year, month }
    });
  } else {
    // Fallback to current route info or default to current date
    const currentYear = routeYear.value || new Date().getFullYear().toString();
    const currentMonth = routeMonth.value || (new Date().getMonth() + 1).toString().padStart(2, '0');
    
    console.log(`Fallback navigation to year: ${currentYear}, month: ${currentMonth}`);
    await router.push({
      name: 'ViewComplaintsByMonth',
      params: { 
        year: currentYear, 
        month: currentMonth 
      }
    });
  }
};

const onQuickSearch = (event) => {
    quickSearch.value = event.target.value;
};

// Watch for changes in route params and fetch complaints accordingly
watch(
  [routeYear, routeMonth, routeTicketId, routeEmployeeId],
  async () => {

    await fetchComplaints();
    
    // Set selectedDate based on routeYear and routeMonth
    if (routeYear.value && routeMonth.value) {
      const year = parseInt(routeYear.value);
      const month = parseInt(routeMonth.value) - 1; // JS months are 0-indexed
      selectedDate.value = new Date(year, month, 1);
    }
    
    // Set selectedEmployee based on routeEmployeeId
    if (routeEmployeeId.value && employeeNames.value.length > 0 && route.path.includes('/complaints/all')) {
      const employeeId = parseInt(routeEmployeeId.value);
      const matchingEmployee = employeeNames.value.find(
        emp => emp.employee_id === employeeId
      );
      if (matchingEmployee) {
        selectedEmployee.value = matchingEmployee;
      }
    }
  },
  { immediate: true }
);

const handleBackToList = () => {
  // If we have year and month in the route, navigate to that month's view
  if (routeYear.value && routeMonth.value) {
    router.push({
      name: 'ViewComplaintsByMonth',
      params: {
        year: routeYear.value,
        month: routeMonth.value
      }
    });
  } else {
    // Otherwise, navigate to the base complaints route
    router.push({ name: 'ViewComplaints' });
  }
};

const handleTicketLoadingChange = (isLoading) => {
  ticketLoading.value = isLoading;
};

onMounted(async () => {
    await fetchEmployeeData();
    searchQuery.value = '';
});
</script>

<style>
.complaints-view .p-tabview .p-tabview-nav li{
    margin-right: 8px;
}
</style>
